import { ProofFileBox } from '@dealstack/frontend/investor/pages/Investment/common/ProofFileBox/ProofFileBox';
import { FormPair } from '@dealstack/frontend/investor/pages/Investment/common/InvestorKycFormsTable/types';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { LastVerifiedBadge } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/LastVerifiedBadge';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { Button, Flex, Text } from '@mantine/core';

interface Props {
	inheritedForms: FormPair[];
	onConfirm?: () => void;
	onDismiss: () => void;
	onReset?: () => void;
	confirmed?: boolean;
}

export const InheritedFormsConfirmation = ({
	inheritedForms,
	onConfirm,
	onDismiss,
	onReset,
	confirmed,
}: Props) => {
	const tInvestor = useTranslationInvestor();

	return (
		<Flex direction="column" gap="1rem" maw="40rem">
			<Flex direction="column" gap="0.25rem">
				<Text fw={700}>{tInvestor('investment.join.kyc.inherited.title')}</Text>
				<Text size="sm" c="dark.0">
					{tInvestor('investment.join.kyc.inherited.description')}
				</Text>
			</Flex>
			<Flex direction="column" gap="0.5rem">
				{inheritedForms.map((form) => (
					<Flex key={form.templateId} direction="column" gap="0.25rem">
						<Flex align="center" gap="0.5rem">
							<Text size="sm" c="dark.0">
								{form.name}
							</Text>
							<LastVerifiedBadge date={form.createdAt} />
						</Flex>
						<ProofFileBox
							fileName={form.fileName ?? form.name}
							fileId={form.fileId ?? form.templateFileId}
							onDelete={() => {
								/* no individual delete in confirmation screen */
							}}
							date={form.createdAt}
						/>
					</Flex>
				))}
			</Flex>
			{confirmed ? (
				<Flex>
					<Button
						size="sm"
						variant="outline"
						color="dark"
						onClick={onReset ?? onDismiss}
						data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_INHERITED_DISMISS_BUTTON}
					>
						{tInvestor('investment.join.kyc.inherited.reset')}
					</Button>
				</Flex>
			) : (
				<Flex gap="0.5rem">
					<Button
						size="sm"
						variant="outline"
						color="dark"
						onClick={onDismiss}
						data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_INHERITED_DISMISS_BUTTON}
					>
						{tInvestor('investment.join.kyc.inherited.no')}
					</Button>
					<Button
						size="sm"
						variant="filled"
						color="blue"
						onClick={onConfirm}
						leftSection={<Icon variant="tick" size="1rem" />}
						data-testid={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_INHERITED_CONFIRM_BUTTON}
					>
						{tInvestor('investment.join.kyc.inherited.yes')}
					</Button>
				</Flex>
			)}
		</Flex>
	);
};
